import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faArrowDown,
  faChartBar,
  faUserTie,
  faUsers,
  faTags,
  faSearch,
  faPlusCircle,
  faEnvelope,
  faMapMarkerAlt,
  faChevronDown,
  faChevronUp,
  faStar,
  faCoins
} from "@fortawesome/free-solid-svg-icons";

import Container from "../../components/UI/Container/Container";
import Spinner from "../../components/UI/Spinner/Spinner";
import Button, { EButtonColor } from "../../components/UI/Button/Button";
import Alert from "../../components/UI/Alert/Alert";
import SearchSelect, { IOption } from "../../components/UI/SearchSelect/SearchSelect";
import { useAuthUser } from "../../hooks/useAuthUser";
import { IAppState } from "../../interfaces";
import * as actions from "../../store/actions";
import { Routes } from "../../classes/Routes";
import {
  getReportData,
  getMatchingProviders,
  IReportResponse,
  IMunicipalityGap,
  IMatchingProvider
} from "../../services/reportsService";
import { ETranslation } from "../../translations/translation-keys";
import classes from "./Reports.module.scss";

interface ISavedSearch {
  id: string;
  name: string;
  services: IOption[];
  weeks: number;
}

interface IProps extends RouteComponentProps {}

const Reports: React.FC<IProps> = (props) => {
  const { history } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const authUser = useAuthUser();

  const { services, loadingServices } = useSelector((state: IAppState) => ({
    services: state.services.services,
    loadingServices: state.services.loading
  }));

  const [selectedServices, setSelectedServices] = useState<IOption[]>([]);
  const [weeks, setWeeks] = useState<number>(4);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<IReportResponse | null>(null);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const [hoveredAttributionKey, setHoveredAttributionKey] = useState<string | null>(null);
  const [hoveredProviderIndex, setHoveredProviderIndex] = useState<number | null>(null);

  // Demand gaps: avattu kunta ja ladatut palveluntarjoajat
  const [expandedMunicipalityKey, setExpandedMunicipalityKey] = useState<string | null>(null);
  const [loadingProvidersKey, setLoadingProvidersKey] = useState<string | null>(null);
  const [providersCache, setProvidersCache] = useState<{ [key: string]: IMatchingProvider[] }>({});

  const handleToggleMunicipality = async (mun: IMunicipalityGap) => {
    const key = `${mun.municipality}_${mun.serviceId ?? 0}`;
    if (expandedMunicipalityKey === key) {
      setExpandedMunicipalityKey(null);
      return;
    }
    setExpandedMunicipalityKey(key);

    if (!providersCache[key]) {
      setLoadingProvidersKey(key);
      try {
        const res = await getMatchingProviders(
          mun.municipality,
          mun.serviceId,
          mun.latitude,
          mun.longitude
        );
        if (res && res.providers) {
          setProvidersCache(prev => ({ ...prev, [key]: res.providers }));
        } else {
          setProvidersCache(prev => ({ ...prev, [key]: [] }));
        }
      } catch (err) {
        console.error("Virhe haettaessa kattavia kumppaneita:", err);
        setProvidersCache(prev => ({ ...prev, [key]: [] }));
      } finally {
        setLoadingProvidersKey(null);
      }
    }
  };

  // Tallennetut hakuehdot (localStorage käyttäjäkohtaisesti)
  const [savedSearches, setSavedSearches] = useState<ISavedSearch[]>([]);
  const [selectedSavedSearchId, setSelectedSavedSearchId] = useState<string>("");

  const storageKey = useMemo(() => {
    const uid = authUser?.id || authUser?.firebaseUserId || authUser?.email;
    return uid ? `hankix_saved_reports_searches_${uid}` : "hankix_saved_reports_searches";
  }, [authUser]);

  // Lataa tallennetut haut käyttäjän vaihtuessa tai komponentin latautuessa
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setSavedSearches(JSON.parse(stored));
      } else {
        setSavedSearches([]);
      }
    } catch (e) {
      console.error("Virhe ladattaessa tallennettuja hakuja localStoragesta:", e);
      setSavedSearches([]);
    }
  }, [storageKey]);

  // Käyttöoikeudet
  const isServiceProvider = Boolean(authUser?.isServiceProvider || authUser?.organization?.level === "SERVICE_PROVIDER");
  const isOperator = Boolean(authUser?.isOperator || authUser?.organization?.level === "OPERATOR");

  // Lataa palvelut alussa
  useEffect(() => {
    if (!services) {
      dispatch(actions.listServices());
    }
  }, [dispatch, services]);

  // Suodatetaan näytettävät palvelut käyttäjän oikeuksien mukaan
  const filteredServices = useMemo(() => {
    if (!services) return [];
    if (isServiceProvider && authUser?.organization?.services) {
      return services.filter(s => authUser.organization.services!.some(as => String(as.id) === String(s.id)));
    }
    if (isOperator) {
      const operatorOrgId = authUser?.organization?.id;
      return services.filter(s => {
        if (operatorOrgId && String(s.operator?.id) === String(operatorOrgId)) {
          return true;
        }
        if (authUser?.organization?.services && authUser.organization.services.some(as => String(as.id) === String(s.id))) {
          return true;
        }
        return false;
      });
    }
    return services;
  }, [services, isServiceProvider, isOperator, authUser]);

  // Hae raporttitiedot
  const fetchData = useCallback((servicesToFetch = selectedServices, weeksToFetch = weeks) => {
    if (servicesToFetch && servicesToFetch.length > 0) {
      setLoading(true);
      setError(null);
      const serviceIds = servicesToFetch.map((s: any) => s.value);
      getReportData(serviceIds, undefined, weeksToFetch)
        .then(res => {
          if (res) {
            setReportData(res);
          } else {
            setError("Raporttitietojen haku epäonnistui taustajärjestelmästä.");
          }
          setLoading(false);
        })
        .catch(err => {
          console.error(err);
          setError("Tietokantavirhe tai puuttuva yhteys taustajärjestelmään.");
          setLoading(false);
        });
    } else {
      setReportData(null);
    }
  }, [selectedServices, weeks]);

  // Tallentaa nykyiset hakuehdot localstorageen käyttäjäkohtaisesti
  const handleSaveSearch = () => {
    let weekPrefix = "12vko";
    if (weeks === 4) weekPrefix = "4vko";
    else if (weeks === 24) weekPrefix = "6kk";
    else if (weeks === 52) weekPrefix = "12kk";

    let name = `${weekPrefix} Ei palveluita`;
    if (selectedServices.length === 1) {
      name = `${weekPrefix} ${selectedServices[0].label}`;
    } else if (selectedServices.length > 1) {
      name = `${weekPrefix} ${selectedServices.length} palvelua`;
    }

    const newSearch: ISavedSearch = {
      id: Date.now().toString(),
      name,
      services: selectedServices,
      weeks
    };

    const updated = [...savedSearches, newSearch];
    setSavedSearches(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
    setSelectedSavedSearchId(newSearch.id);
  };

  // Lataa tallennetun haun tiedot suoraan käyttöön (onChange latauksella)
  const handleSelectSavedSearch = (id: string) => {
    setSelectedSavedSearchId(id);
    if (!id) return;
    const match = savedSearches.find(s => s.id === id);
    if (match) {
      // Suodatetaan tallennetusta hausta vain nykyiselle käyttäjälle sallitut palvelut
      const validServices = match.services.filter(ms =>
        filteredServices.some(fs => String(fs.id) === String(ms.value))
      );
      const targetServices = validServices.length > 0
        ? validServices
        : filteredServices.map((s: any) => ({ value: s.id, label: s.name }));

      setSelectedServices(targetServices);
      setWeeks(match.weeks);
      fetchData(targetServices, match.weeks);
    }
  };

  // Poistaa tallennetun haun localstoragesta
  const handleDeleteSavedSearch = (id: string) => {
    const updated = savedSearches.filter(s => s.id !== id);
    setSavedSearches(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
    if (selectedSavedSearchId === id) {
      setSelectedSavedSearchId("");
    }
  };

  // Valitsee kaikki tarjolla olevat palvelut suodattimeen kerralla
  const handleSelectAllServices = () => {
    const allOptions = filteredServices.map((s: any) => ({
      value: s.id,
      label: s.name
    }));
    setSelectedServices(allOptions);
  };

  // Aseta oletuspalvelut ja tee ensihaku kun palvelut on ladattu
  useEffect(() => {
    if (filteredServices && filteredServices.length > 0) {
      if (selectedServices.length === 0) {
        const initialOptions = filteredServices.map((s: any) => ({
          value: s.id,
          label: s.name
        }));
        setSelectedServices(initialOptions);
        fetchData(initialOptions, weeks);
      } else {
        // Suodatetaan mahdolliset toisen käyttäjän/istunnon palvelut
        const validServices = selectedServices.filter(ss =>
          filteredServices.some(fs => String(fs.id) === String(ss.value))
        );
        if (validServices.length !== selectedServices.length) {
          const nextServices = validServices.length > 0
            ? validServices
            : filteredServices.map((s: any) => ({ value: s.id, label: s.name }));
          setSelectedServices(nextServices);
          fetchData(nextServices, weeks);
        }
      }
    }
  }, [filteredServices, selectedServices, weeks, fetchData]);

  // --- SVG Kuvaajalaskennat (Custom Interactive SVG Charts) ---

  // A. Liidit & Kasvutrendi (Bar & Line Combo)
  const comboChartDimensions = { width: 700, height: 300, paddingLeft: 50, paddingRight: 50, paddingTop: 30, paddingBottom: 45 };
  const comboData = useMemo(() => reportData?.timeSeries || [], [reportData?.timeSeries]);

  const maxLeads = useMemo(() => {
    if (comboData.length === 0) return 10;
    return Math.max(...comboData.map(d => d.leads), 1);
  }, [comboData]);

  // B. Hankintakanavat (Donut Chart)
  const donutDimensions = { size: 240, radius: 100, innerRadius: 65, center: 120 };
  const attributionData = useMemo(() => reportData?.attribution || { DIRECT: 0, AI: 0, PARTNERS: 0, SEARCH: 0, OTHER: 0 }, [reportData?.attribution]);
  const totalAttributed = Object.values(attributionData).reduce((a, b) => a + b, 0);

  const donutSectors = useMemo(() => {
    let accumulatedAngle = 0;
    return Object.entries(attributionData).map(([key, value]) => {
      const percentage = totalAttributed > 0 ? (value / totalAttributed) : 0;
      const angle = percentage * 360;
      const startAngle = accumulatedAngle;
      accumulatedAngle += angle;
      return {
        key,
        value,
        percentage,
        startAngle,
        endAngle: accumulatedAngle
      };
    });
  }, [attributionData, totalAttributed]);

  const getDonutPath = (startAngle: number, endAngle: number) => {
    if (endAngle - startAngle >= 359.99) {
      // Piirretään täysi rengas
      return `M ${donutDimensions.center} ${donutDimensions.center - donutDimensions.radius} 
              A ${donutDimensions.radius} ${donutDimensions.radius} 0 1 1 ${donutDimensions.center - 0.01} ${donutDimensions.center - donutDimensions.radius}
              M ${donutDimensions.center} ${donutDimensions.center - donutDimensions.innerRadius}
              A ${donutDimensions.innerRadius} ${donutDimensions.innerRadius} 0 1 0 ${donutDimensions.center - 0.01} ${donutDimensions.center - donutDimensions.innerRadius}`;
    }

    const rad = Math.PI / 180;
    // Kulmien korjaus jotta 0-kulma on ylhäällä
    const sAngle = startAngle - 90;
    const eAngle = endAngle - 90;

    const x1 = donutDimensions.center + donutDimensions.radius * Math.cos(sAngle * rad);
    const y1 = donutDimensions.center + donutDimensions.radius * Math.sin(sAngle * rad);
    const x2 = donutDimensions.center + donutDimensions.radius * Math.cos(eAngle * rad);
    const y2 = donutDimensions.center + donutDimensions.radius * Math.sin(eAngle * rad);

    const x3 = donutDimensions.center + donutDimensions.innerRadius * Math.cos(eAngle * rad);
    const y3 = donutDimensions.center + donutDimensions.innerRadius * Math.sin(eAngle * rad);
    const x4 = donutDimensions.center + donutDimensions.innerRadius * Math.cos(sAngle * rad);
    const y4 = donutDimensions.center + donutDimensions.innerRadius * Math.sin(sAngle * rad);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${x1} ${y1} 
            A ${donutDimensions.radius} ${donutDimensions.radius} 0 ${largeArc} 1 ${x2} ${y2}
            L ${x3} ${y3}
            A ${donutDimensions.innerRadius} ${donutDimensions.innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;
  };

  const attributionColors: Record<string, string> = {
    DIRECT: "#8b5cf6",  // Violetti
    AI: "#06b6d4",      // Turkoosi (Gemini/AI vibe)
    PARTNERS: "#10b981",// Vihreä (Kumppanit)
    SEARCH: "#3b82f6",  // Sininen (Haku)
    OTHER: "#f59e0b"    // Oranssi
  };

  const attributionLabels: Record<string, string> = {
    DIRECT: "Suora liikenne",
    AI: "Tekoäly (AI)",
    PARTNERS: "Kumppanit",
    SEARCH: "Hakukoneet",
    OTHER: "Muut / Somelinkit"
  };

  // C. Yrittäjien Ansaintatyyppien jakaumakehitys (Stacked Bar Chart) - Vain Operator/Admin
  const providerChartDimensions = { width: 700, height: 300, paddingLeft: 50, paddingRight: 40, paddingTop: 30, paddingBottom: 45 };
  const maxProviders = useMemo(() => {
    if (comboData.length === 0) return 10;
    return Math.max(...comboData.map(d => d.subscriptionProviders + d.microProviders), 1);
  }, [comboData]);

  // D. Palveluntarjoajien ansaintamallijakauma (4 saraketta)
  const providerBreakdown = reportData?.providerBreakdown;
  const credentialedMonth = providerBreakdown?.credentialed.month ?? reportData?.summary.credentialedMonthProviders ?? 0;
  const credentialedMicro = providerBreakdown?.credentialed.micro ?? reportData?.summary.credentialedMicroProviders ?? 0;
  const credentialedTotal = providerBreakdown?.credentialed.total ?? (credentialedMonth + credentialedMicro);
  const credentialedMonthPct = providerBreakdown?.credentialed.monthPercent ?? (
    credentialedTotal > 0 ? (credentialedMonth / credentialedTotal) * 100 : 0
  );
  const credentialedMicroPct = providerBreakdown?.credentialed.microPercent ?? (
    credentialedTotal > 0 ? (credentialedMicro / credentialedTotal) * 100 : 0
  );

  const allMonth = providerBreakdown?.all.month ?? reportData?.summary.allMonthProviders ?? 0;
  const allMicro = providerBreakdown?.all.micro ?? reportData?.summary.allMicroProviders ?? 0;
  const allTotal = providerBreakdown?.all.total ?? (allMonth + allMicro);
  const allMonthPct = providerBreakdown?.all.monthPercent ?? (
    allTotal > 0 ? (allMonth / allTotal) * 100 : 0
  );
  const allMicroPct = providerBreakdown?.all.microPercent ?? (
    allTotal > 0 ? (allMicro / allTotal) * 100 : 0
  );

  const adoptionPct = providerBreakdown?.credentialedAdoptionPercent ?? reportData?.summary.credentialedAdoptionPercent ?? (
    allTotal > 0 ? (credentialedTotal / allTotal) * 100 : 0
  );

  // Loading spinner
  if (loadingServices) {
    return (
      <Container style={{ textAlign: "center", padding: "3rem 0" }}>
        <Spinner />
      </Container>
    );
  }

  return (
    <>
      {error && <Alert children={error} />}
      
      <div className={classes.DashboardContainer}>
        {/* Yläpalkki ja suodattimet */}
        <div className={classes.DashboardHeader}>
          <div>
            <h1>{t(ETranslation.REPORTS) || "Raportointi"}</h1>
            <p className={classes.Subtitle}>
              {isServiceProvider 
                ? "Yrityksellesi tilattujen palveluiden yleinen aktiviteettikehitys Hankixissa." 
                : "Seuraa tarjouspyyntö virtaa, liikenteen lähteitä ja käyttäjäaktiviteettia."}
            </p>
          </div>

          <div className={classes.FilterRow}>
            {/* Palveluvalitsin */}
            <div className={classes.FilterControl} style={{ minWidth: "260px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label>{t(ETranslation.COMMON_SERVICE) || "Palvelu"}</label>
                <button
                  onClick={handleSelectAllServices}
                  title="Lisää kaikki"
                  style={{
                    background: "none",
                    border: "none",
                    color: "#8b5cf6",
                    cursor: "pointer",
                    padding: "0 0 0.25rem 0",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "0.85rem",
                    transition: "color 0.2s ease"
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#6d28d9")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#8b5cf6")}
                >
                  <FontAwesomeIcon icon={faPlusCircle} />
                </button>
              </div>
              <SearchSelect
                inputName="serviceSelect"
                placeholder="Valitse palvelut..."
                multiple={true}
                options={filteredServices.map((s) => ({ value: s.id, label: s.name }))}
                value={selectedServices}
                onChange={(val) => setSelectedServices(val as IOption[])}
              />
            </div>

            {/* Viikkovalitsin */}
            <div className={classes.FilterControl}>
              <label htmlFor="weeksSelect">Aikarajaus</label>
              <select
                id="weeksSelect"
                value={weeks}
                onChange={(e) => {
                  const newWeeks = Number(e.target.value);
                  setWeeks(newWeeks);
                  if (selectedServices.length > 0) {
                    fetchData(selectedServices, newWeeks);
                  }
                }}
              >
                <option value={4}>Viimeiset 4 viikkoa / 30 vrk (Vakio)</option>
                <option value={12}>Viimeiset 12 viikkoa</option>
                <option value={24}>Viimeiset 6 kuukautta</option>
                <option value={52}>Viimeiset 12 kuukautta</option>
              </select>
            </div>

            {/* Hae-painike */}
            <div className={classes.FilterControl}>
              <label style={{ visibility: "hidden" }}>Hae</label>
              <Button
                onClick={() => fetchData(selectedServices, weeks)}
                disabled={selectedServices.length === 0 || loading}
                loading={loading}
                icon={faSearch}
              >
                Hae
              </Button>
            </div>

            {/* Tallenna haku -painike */}
            <div className={classes.FilterControl}>
              <label style={{ visibility: "hidden" }}>Tallenna</label>
              <Button
                onClick={handleSaveSearch}
                disabled={selectedServices.length === 0}
                color={EButtonColor.SECONDARY}
              >
                Tallenna haku
              </Button>
            </div>

            {/* Tallennetut haut -valitsin */}
            <div className={classes.FilterControl} style={{ minWidth: "220px" }}>
              <label htmlFor="savedSearchesSelect">Tallennetut haut</label>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <select
                  id="savedSearchesSelect"
                  value={selectedSavedSearchId}
                  onChange={(e) => handleSelectSavedSearch(e.target.value)}
                  style={{ flexGrow: 1 }}
                >
                  <option value="">Valitse tallennettu haku...</option>
                  {savedSearches.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {selectedSavedSearchId && (
                  <button
                    onClick={() => handleDeleteSavedSearch(selectedSavedSearchId)}
                    title="Poista tallennettu haku"
                    style={{
                      background: "none",
                      border: "none",
                      color: "#ef4444",
                      cursor: "pointer",
                      fontSize: "1.1rem",
                      padding: "0 0.25rem",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "5rem 0" }}>
            <Spinner />
          </div>
        ) : reportData ? (
          <>
            {/* KPI avainlukukortit lasiefektillä (Glassmorphic KPI Cards) */}
            <div className={classes.KpiGrid}>
              
              {/* Tämän viikon tarjouspyynnöt */}
              <div className={classes.KpiCard}>
                <div className={classes.CardHeader}>
                  <span>Tarjouspyynnöt (viikko)</span>
                  <div className={classes.IconContainer} style={{ background: "rgba(139, 92, 246, 0.15)", color: "#8b5cf6" }}>
                    <FontAwesomeIcon icon={faChartBar} />
                  </div>
                </div>
                <div className={classes.CardBody}>
                  <div className={classes.Number}>{reportData.summary.currentWeekLeads}</div>
                  <div className={`${classes.Trend} ${reportData.summary.leadChangePercent >= 0 ? classes.Positive : classes.Negative}`}>
                    <FontAwesomeIcon icon={reportData.summary.leadChangePercent >= 0 ? faArrowUp : faArrowDown} />
                    <span>{Math.abs(reportData.summary.leadChangePercent).toFixed(1)}%</span>
                    <span className={classes.TrendLabel}>ed. viikkoon</span>
                  </div>
                </div>
              </div>

              {/* Kumulatiiviset tarjouspyynnöt */}
              <div className={classes.KpiCard}>
                <div className={classes.CardHeader}>
                  <span>Tarjouspyynnöt Yhteensä (jaksolla)</span>
                  <div className={classes.IconContainer} style={{ background: "rgba(6, 182, 212, 0.15)", color: "#06b6d4" }}>
                    <FontAwesomeIcon icon={faTags} />
                  </div>
                </div>
                <div className={classes.CardBody}>
                  <div className={classes.Number}>{reportData.summary.totalLeads}</div>
                  <div className={classes.InfoLabel}>Kerättyjä tarjouspyyntöjä</div>
                </div>
              </div>

              {/* Aktiiviset kumppanit */}
              <div className={classes.KpiCard}>
                <div className={classes.CardHeader}>
                  <span>Palveluntarjoajat</span>
                  <div className={classes.IconContainer} style={{ background: "rgba(16, 185, 129, 0.15)", color: "#10b981" }}>
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                </div>
                <div className={classes.CardBody}>
                  <div className={classes.Number}>{reportData.summary.activeProviders}</div>
                  <div className={classes.InfoLabel}>Aktiivisia yrityksiä alueella</div>
                </div>
              </div>

              {/* Laskutusprofiili (vain Primary/Operator) tai Aktiviteettiluku (Service Provider) */}
              {!isServiceProvider ? (
                <div className={classes.KpiCard}>
                  <div className={classes.CardHeader}>
                    <span>Laskutusprofiili</span>
                    <div className={classes.IconContainer} style={{ background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}>
                      <FontAwesomeIcon icon={faUserTie} />
                    </div>
                  </div>
                  <div className={classes.CardBody}>
                    <div className={classes.NumberSplit}>
                      <div>
                        <span className={classes.SmallVal}>{reportData.summary.subscriptionPercent.toFixed(0)}%</span>
                        <span className={classes.LabelVal}>Kk-maksu</span>
                      </div>
                      <div className={classes.Divider} />
                      <div>
                        <span className={classes.SmallVal}>{reportData.summary.microPercent.toFixed(0)}%</span>
                        <span className={classes.LabelVal}>Mikro</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={classes.KpiCard}>
                  <div className={classes.CardHeader}>
                    <span>Aktiviteetti yhteensä</span>
                    <div className={classes.IconContainer} style={{ background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}>
                      <FontAwesomeIcon icon={faUserTie} />
                    </div>
                  </div>
                  <div className={classes.CardBody}>
                    <div className={classes.Number}>
                      {comboData.reduce((acc, curr) => acc + curr.totalActivity, 0)}
                    </div>
                    <div className={classes.InfoLabel}>Tapahtumia järjestelmässä</div>
                  </div>
                </div>
              )}

              {/* Asiakastyytyväisyys & Laatu (Feedback KPI) */}
              {reportData.feedback && (
                <div className={classes.KpiCard}>
                  <div className={classes.CardHeader}>
                    <span>Laatu ja palaute</span>
                    <div className={classes.IconContainer} style={{ background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}>
                      <FontAwesomeIcon icon={faStar} />
                    </div>
                  </div>
                  <div className={classes.CardBody}>
                    <div className={classes.FeedbackRatingRow}>
                      <span className={classes.FeedbackRatingNumber}>
                        {reportData.feedback.averageRating > 0 ? reportData.feedback.averageRating.toFixed(1) : "-"}
                      </span>
                      <div className={classes.StarsContainer}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FontAwesomeIcon
                            key={star}
                            icon={faStar}
                            className={
                              star <= Math.round(reportData.feedback?.averageRating || 0)
                                ? classes.StarFilled
                                : classes.StarEmpty
                            }
                          />
                        ))}
                      </div>
                    </div>
                    <div className={classes.FeedbackSubRow}>
                      <span className={classes.InfoLabel}>
                        {reportData.feedback.periodFeedbackCount} kpl jaksolla ({reportData.feedback.totalFeedbackCount} yht.)
                      </span>
                      {reportData.feedback.periodFeedbackCount > 0 && (
                        <span className={classes.FeedbackBadge}>
                          {reportData.feedback.positivePercent.toFixed(0)}% pos.
                        </span>
                      )}
                    </div>
                    {history && (
                      <button
                        type="button"
                        className={classes.FeedbackActionBtn}
                        onClick={() => history.push(Routes.FEEDBACKS)}
                      >
                        Katso palautteet &rarr;
                      </button>
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* Kaaviograafit */}
            <div className={classes.ChartsGrid}>
              
              {/* 1. Liidivolyymi (Puhdas viikkovolyymipylväskaavio) */}
              <div className={classes.ChartCard}>
                <h3>Tarjouspyyntöjen viikkovolyymi</h3>
                <div className={classes.SvgWrapper}>
                  <svg 
                    viewBox={`0 0 ${comboChartDimensions.width} ${comboChartDimensions.height}`}
                    className={classes.InteractiveSvg}
                  >
                    <defs>
                      <linearGradient id="leadsBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
                      </linearGradient>
                    </defs>

                    {/* Taustaviivat (Akselit) */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
                      const y = comboChartDimensions.paddingTop + 
                        (comboChartDimensions.height - comboChartDimensions.paddingTop - comboChartDimensions.paddingBottom) * ratio;
                      return (
                        <line 
                          key={index}
                          x1={comboChartDimensions.paddingLeft}
                          y1={y}
                          x2={comboChartDimensions.width - comboChartDimensions.paddingRight}
                          y2={y}
                          stroke="rgba(255,255,255,0.06)"
                          strokeWidth="1"
                        />
                      );
                    })}

                    {/* Weekly Bars (Liidit) */}
                    {comboData.map((d, index) => {
                      const totalWeeks = comboData.length;
                      const chartWidth = comboChartDimensions.width - comboChartDimensions.paddingLeft - comboChartDimensions.paddingRight;
                      const colWidth = chartWidth / totalWeeks;
                      
                      const x = comboChartDimensions.paddingLeft + colWidth * index + (colWidth * 0.15);
                      const barWidth = colWidth * 0.7;
                      
                      const chartHeight = comboChartDimensions.height - comboChartDimensions.paddingTop - comboChartDimensions.paddingBottom;
                      const leadsHeight = (d.leads / maxLeads) * chartHeight;
                      const y = comboChartDimensions.height - comboChartDimensions.paddingBottom - leadsHeight;

                      return (
                        <g 
                          key={index} 
                          onMouseEnter={() => setHoveredBarIndex(index)}
                          onMouseLeave={() => setHoveredBarIndex(null)}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Palkki */}
                          <rect 
                            x={x}
                            y={y}
                            width={barWidth}
                            height={Math.max(leadsHeight, 1)}
                            fill="url(#leadsBarGrad)"
                            rx={3}
                            className={classes.BarElement}
                            style={{ 
                              transition: "all 0.3s ease",
                              fillOpacity: hoveredBarIndex === index ? 1 : 0.8
                            }}
                          />
                          {/* Viikkovolyymi palkin päällä */}
                          {d.leads > 0 && (
                            <text 
                              x={x + barWidth/2}
                              y={y - 6}
                              textAnchor="middle"
                              fill="#8b5cf6"
                              fontSize="11"
                              fontWeight="bold"
                            >
                              {d.leads}
                            </text>
                          )}
                          {/* Viikon tekstiakseli */}
                          {index % (totalWeeks > 15 ? 2 : 1) === 0 && (
                            <text 
                              x={x + barWidth/2}
                              y={comboChartDimensions.height - 15}
                              textAnchor="middle"
                              fill="#888"
                              fontSize="11"
                            >
                              {d.weekLabel}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* Y-akselien arvot */}
                    <text x={10} y={comboChartDimensions.paddingTop + 10} fill="#888" fontSize="10" fontWeight="bold">Liidit (viikko)</text>
                  </svg>
                </div>

                {/* Siisti, dynaaminen Tooltip/Tietolaatikko palkin kohdistukselle */}
                <div className={classes.ChartTooltipBox}>
                  {hoveredBarIndex !== null ? (
                    <div className={classes.TooltipContent}>
                      <span className={classes.TooltipWeek}>{comboData[hoveredBarIndex].weekLabel} ({comboData[hoveredBarIndex].startDate})</span>
                      <div className={classes.TooltipRows}>
                        <div><strong>Viikon tarjouspyynnöt:</strong> <span style={{ color: "#8b5cf6", fontWeight: "bold" }}>{comboData[hoveredBarIndex].leads} kpl</span></div>
                        {comboData[hoveredBarIndex].totalActivity > 0 && (
                          <div><strong>Aktiviteettiloki:</strong> <span style={{ color: "#f59e0b", fontWeight: "bold" }}>{comboData[hoveredBarIndex].totalActivity} kpl</span></div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={classes.TooltipPlaceholder}>
                      Vie hiiri kuvaajan palkkien päälle nähdäksesi viikkokohtaiset tiedot.
                    </div>
                  )}
                </div>
              </div>

              {/* 2. Liikenteen hankintalähteet (Source Attribution Donut) */}
              <div className={classes.ChartCard}>
                <h3>Liikenteen hankintakanavat (Attribuutio)</h3>
                
                {totalAttributed > 0 ? (
                  <div className={classes.DonutLayout}>
                    <div className={classes.DonutWrapper}>
                      <svg viewBox={`0 0 ${donutDimensions.size} ${donutDimensions.size}`} className={classes.InteractiveSvg}>
                        {donutSectors.map((sector) => {
                          if (sector.value === 0) return null;
                          const isHovered = hoveredAttributionKey === sector.key;
                          
                          return (
                            <path 
                              key={sector.key}
                              d={getDonutPath(sector.startAngle, sector.endAngle)}
                              fill={attributionColors[sector.key]}
                              fillOpacity={hoveredAttributionKey ? (isHovered ? 1 : 0.4) : 0.85}
                              style={{ 
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                transform: isHovered ? "scale(1.03)" : "scale(1)",
                                transformOrigin: `${donutDimensions.center}px ${donutDimensions.center}px`
                              }}
                              onMouseEnter={() => setHoveredAttributionKey(sector.key)}
                              onMouseLeave={() => setHoveredAttributionKey(null)}
                            />
                          );
                        })}
                        {/* Keskiaukon teksti */}
                        <circle cx={donutDimensions.center} cy={donutDimensions.center} r={donutDimensions.innerRadius - 2} fill="#fff" />
                        <text x={donutDimensions.center} y={donutDimensions.center - 5} textAnchor="middle" fill="#888" fontSize="11" fontWeight="bold">YHTEENSÄ</text>
                        <text x={donutDimensions.center} y={donutDimensions.center + 18} textAnchor="middle" fill="#222" fontSize="22" fontWeight="bold">
                          {totalAttributed}
                        </text>
                      </svg>
                    </div>

                    {/* Interaktiivinen selite (Legend) */}
                    <div className={classes.DonutLegend}>
                      {donutSectors.map((sector) => {
                        const isHovered = hoveredAttributionKey === sector.key;
                        return (
                          <div 
                            key={sector.key} 
                            className={`${classes.LegendRow} ${isHovered ? classes.LegendRowActive : ""}`}
                            onMouseEnter={() => setHoveredAttributionKey(sector.key)}
                            onMouseLeave={() => setHoveredAttributionKey(null)}
                          >
                            <span className={classes.Dot} style={{ background: attributionColors[sector.key] }} />
                            <div className={classes.LegendText}>
                              <span className={classes.Label}>{attributionLabels[sector.key]}</span>
                              <span className={classes.Val}>{sector.value} kpl ({(sector.percentage * 100).toFixed(0)}%)</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className={classes.EmptyChartMessage}>
                    Ei attribuutiotietoja haetulta aikaväliltä.
                  </div>
                )}
              </div>

            </div>

            {/* 2.5 Kysyntä- ja puutedatan näkymä (Demand Gaps) - Vain Primary/Operator */}
            {!isServiceProvider && reportData && (
              <div className={classes.DemandGapsCard}>
                <h3>Kysyntä- ja puuteanalyysi (Demand Gap & Palvelualuekattavuus)</h3>
                <p className={classes.Subtitle}>
                  Katselukattavuus (View Count) ja avaamattomat (0 katselua) tarjouspyynnöt paikkakunnittain sekä alueen kattavat kumppanit (toimintasäde 350 km).
                </p>

                {reportData.demandGaps ? (
                  <>
                    <div className={classes.DemandKpiRow}>
                      <div className={classes.DemandKpiItem}>
                        <span className={classes.KpiLabel}>Katselukattavuus (View Rate)</span>
                        <span className={`${classes.KpiValue} ${classes.Success}`}>
                          {reportData.demandGaps.matchRatePercent.toFixed(1)}%
                        </span>
                        <span className={classes.KpiSub}>
                          {reportData.demandGaps.matchedLeads} kpl yrittäjien katsomia / käsiteltyjä (30 pv)
                        </span>
                      </div>

                      <div className={classes.DemandKpiItem}>
                        <span className={classes.KpiLabel}>Avaamattomat (Katselukerrat 0)</span>
                        <span className={`${classes.KpiValue} ${classes.Warning}`}>
                          {reportData.demandGaps.zeroOfferRatePercent.toFixed(1)}%
                        </span>
                        <span className={classes.KpiSub}>
                          {reportData.demandGaps.needsHelpLeads} kpl täysin avaamattomia (30 pv)
                        </span>
                      </div>

                      <div className={classes.DemandKpiItem}>
                        <span className={classes.KpiLabel}>Puutepaikkakuntia</span>
                        <span className={classes.KpiValue}>
                          {reportData.demandGaps.municipalities.length} kpl
                        </span>
                        <span className={classes.KpiSub}>
                          Kohteita, joissa avaamattomia pyyntöjä
                        </span>
                      </div>
                    </div>

                    {reportData.demandGaps.municipalities.length > 0 ? (
                      <div className={classes.GapsTableContainer}>
                        <table className={classes.GapsTable}>
                          <thead>
                            <tr>
                              <th>Paikkakunta / Kunta</th>
                              <th>Palvelu</th>
                              <th>Avaamattomat (0 katselua)</th>
                              <th>Tarjoukset (kpl)</th>
                              <th>Alueen palveluntarjoajat</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.demandGaps.municipalities.map(mun => {
                              const key = `${mun.municipality}_${mun.serviceId ?? 0}`;
                              const isExpanded = expandedMunicipalityKey === key;
                              const isLoadingProviders = loadingProvidersKey === key;
                              const providers = providersCache[key];

                              return (
                                <React.Fragment key={key}>
                                  <tr
                                    className={`${classes.MunRow} ${isExpanded ? classes.Expanded : ""}`}
                                    onClick={() => handleToggleMunicipality(mun)}
                                  >
                                    <td>
                                      <span className={classes.MunName}>{mun.municipality}</span>
                                    </td>
                                    <td>{mun.serviceName || "Yleinen"}</td>
                                    <td>
                                      <span className={classes.BadgeZeroViews}>{mun.count} kpl</span>
                                    </td>
                                    <td>
                                      <span style={{ color: mun.offerCount && mun.offerCount > 0 ? "#10b981" : "#94a3b8", fontWeight: 600 }}>
                                        {mun.offerCount ?? 0} kpl
                                      </span>
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className={classes.ExpandBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleMunicipality(mun);
                                        }}
                                      >
                                        <span>{isExpanded ? "Piilota kumppanit" : "Etsi kumppanit"}</span>
                                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                      </button>
                                    </td>
                                  </tr>

                                  {isExpanded && (
                                    <tr className={classes.DetailsRow}>
                                      <td colSpan={5}>
                                        <div className={classes.ProvidersContainer}>
                                          <h4>
                                            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: "#8b5cf6" }} />
                                            Alueen kattavat kumppanit ({mun.municipality})
                                          </h4>

                                          {isLoadingProviders && (
                                            <div className={classes.LoadingInline}>
                                              <Spinner /> Haetaan paikkakunnan kattavia palveluntarjoajia...
                                            </div>
                                          )}

                                          {!isLoadingProviders && providers && providers.length === 0 && (
                                            <div className={classes.EmptyProviders}>
                                              Ei rekisteröityjä palveluntarjoajia, joiden toimintasäde kattaisi paikkakunnan {mun.municipality}.
                                            </div>
                                          )}

                                          {!isLoadingProviders && providers && providers.length > 0 && (
                                            <div className={classes.ProvidersList}>
                                              {providers.map(p => (
                                                <div key={p.id} className={classes.ProviderCard}>
                                                  <div className={classes.ProviderHeader}>
                                                    <span className={classes.ProviderName}>{p.name}</span>
                                                    <span className={`${classes.PaymentBadge} ${p.paymentMethod === 'MONTH' ? classes.Subscription : classes.Micro}`}>
                                                      {p.paymentMethod === 'MONTH' ? 'Kuukausitilaaja' : 'Mikromaksu'}
                                                    </span>
                                                  </div>

                                                  <div className={classes.ProviderMeta}>
                                                    <span>
                                                      📍 {p.nearestCity || p.city || "Tuntematon"} ({p.distanceKm} km)
                                                    </span>
                                                    <span>
                                                      🎯 Säde: {p.operatingRadius} km
                                                    </span>
                                                  </div>

                                                  <div className={classes.EmailsList}>
                                                    <span className={classes.EmailsLabel}>Sähköpostiosoitteet yhteydenottoon:</span>
                                                    {p.emails && p.emails.length > 0 ? (
                                                      p.emails.map(email => (
                                                        <a
                                                          key={email}
                                                          href={`mailto:${email}`}
                                                          className={classes.EmailLink}
                                                          onClick={e => e.stopPropagation()}
                                                        >
                                                          <FontAwesomeIcon icon={faEnvelope} /> {email}
                                                        </a>
                                                      ))
                                                    ) : (
                                                      <span className={classes.NoEmail}>Ei sähköpostia saatavilla</span>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className={classes.EmptyChartMessage}>
                        Ei huomiota tarvitsevia tarjouspyyntöjä valitulla ajanjaksolla.
                      </div>
                    )}
                  </>
                ) : (
                  <div className={classes.EmptyChartMessage}>
                    Ei kysyntäpuutetietoja saatavilla.
                  </div>
                )}
              </div>
            )}

            {/* 3. Käyttäjäaktiviteetin kehitys (Audit Trail logs) */}
            <div className={classes.FullWidthCard}>
              <h3>HankiX-käyttöaktiviteetin viikkokehitys (Audit Trail)</h3>
              <div className={classes.ActivityScrollContainer}>
                <table className={classes.ActivityTable}>
                  <thead>
                    <tr>
                      <th>Viikko</th>
                      <th>Luodut tarjouspyynnöt </th>
                      <th>Tarjouspyyntöjen muokkaukset & arkistoinnit</th>
                      <th>Palveluntarjoajien kirjautumiset</th>
                      <th>Mikromaksuostot</th>
                      <th>Uudet tarjoukset (Offer)</th>
                      <th>Yritysprofiilin muokkaukset</th>
                      <th className={classes.TotalCol}>Yhteensä</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...comboData].reverse().map((d, idx) => (
                      <tr key={idx}>
                        <td><strong>{d.weekLabel}</strong> <span className={classes.DateSpan}>({d.startDate})</span></td>
                        <td>{d.activityLeadsCreated > 0 ? d.activityLeadsCreated : "-"}</td>
                        <td>{d.activityLeadsUpdated > 0 ? d.activityLeadsUpdated : "-"}</td>
                        <td>{d.activityLogins > 0 ? d.activityLogins : "-"}</td>
                        <td>{d.activityLeadsOpened > 0 ? d.activityLeadsOpened : "-"}</td>
                        <td>{d.activityOffersSaved > 0 ? d.activityOffersSaved : "-"}</td>
                        <td>{d.activityProfileUpdates > 0 ? d.activityProfileUpdates : "-"}</td>
                        <td className={classes.TotalCol}><strong>{d.totalActivity}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3.5 Liikevaihto ja laskutuksen kokonaiskuva - Vain Primary/Operator */}
            {!isServiceProvider && reportData.revenue && (
              <div className={classes.FullWidthCard} style={{ marginTop: "2rem" }}>
                <div className={classes.RevenueHeaderRow}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.35rem" }}>
                      <span className={classes.BetaBadge}>BETA</span>
                      <h3 style={{ margin: 0 }}>Liikevaihto ja laskutuksen kokonaiskuva</h3>
                    </div>
                    <p className={classes.CardSubtitle}>
                      Toistuva kuukausiliikevaihto (MRR), jakson liikevaihtopotentiaali ja laskutuksen tila kytketään palvelukohtaisten hinnoittelujen vahvistuttua.
                    </p>
                  </div>
                  <div className={classes.RevenueTotalPillInactive}>
                    <FontAwesomeIcon icon={faCoins} style={{ marginRight: "0.5rem" }} />
                    Laskutusdataa ei vielä kytketty
                  </div>
                </div>

                <div className={classes.RevenueGrid}>
                  {/* MRR */}
                  <div className={classes.RevenueCard}>
                    <span className={classes.RevenueCardLabel}>Arvioitu toistuva kk-tulo (MRR)</span>
                    <div>
                      <span className={classes.RevenuePlaceholderValue}>Laskutusdataa ei vielä kytketty</span>
                    </div>
                    <span className={classes.RevenueCardSubtext}>
                      Hinnoittelu määritellään palvelukohtaisesti
                    </span>
                  </div>

                  {/* Kk-tilaukset jaksolla */}
                  <div className={classes.RevenueCard}>
                    <span className={classes.RevenueCardLabel}>Kk-tilausliikevaihto jaksolla</span>
                    <div>
                      <span className={classes.RevenuePlaceholderValue}>Laskutusdataa ei vielä kytketty</span>
                    </div>
                    <span className={classes.RevenueCardSubtext}>
                      Ei dataa valitulta ajanjaksolta
                    </span>
                  </div>

                  {/* Mikromaksut */}
                  <div className={classes.RevenueCard}>
                    <span className={classes.RevenueCardLabel}>Mikromaksuliikevaihto</span>
                    <div>
                      <span className={classes.RevenuePlaceholderValue}>Laskutusdataa ei vielä kytketty</span>
                    </div>
                    <span className={classes.RevenueCardSubtext}>
                      Liidikohtainen mikromaksu määritellään myöhemmin
                    </span>
                  </div>

                  {/* Kokonaisliikevaihto */}
                  <div className={`${classes.RevenueCard} ${classes.RevenueCardHighlight}`}>
                    <span className={classes.RevenueCardLabel}>Kokonaisliikevaihto (jakso)</span>
                    <div>
                      <span className={classes.RevenuePlaceholderValue}>Laskutusdataa ei vielä kytketty</span>
                    </div>
                    <span className={classes.RevenueCardSubtext}>
                      Kokonaislaskutus kytketään integraation yhteydessä
                    </span>
                  </div>
                </div>

                {/* Laskutuksen tilanne */}
                <div className={classes.InvoicingStatusBar}>
                  <div className={classes.InvoicingHeader}>
                    <span className={classes.InvoicingTitle}>Laskutuksen tilanne (reskontra)</span>
                  </div>
                  <div className={classes.InvoicingMetrics}>
                    <div className={classes.InvoicingItem}>
                      <div className={classes.InvoicingDot} style={{ background: "#94a3b8" }} />
                      <div>
                        <span className={classes.InvoicingLabel}>Maksettu</span>
                        <span className={classes.InvoicingVal} style={{ color: "#64748b", fontSize: "0.95rem" }}>
                          Laskutusdataa ei vielä kytketty
                        </span>
                      </div>
                    </div>

                    <div className={classes.InvoicingItem}>
                      <div className={classes.InvoicingDot} style={{ background: "#94a3b8" }} />
                      <div>
                        <span className={classes.InvoicingLabel}>Avoinna (erääntymättä)</span>
                        <span className={classes.InvoicingVal} style={{ color: "#64748b", fontSize: "0.95rem" }}>
                          Laskutusdataa ei vielä kytketty
                        </span>
                      </div>
                    </div>

                    <div className={classes.InvoicingItem}>
                      <div className={classes.InvoicingDot} style={{ background: "#94a3b8" }} />
                      <div>
                        <span className={classes.InvoicingLabel}>Erääntyneet</span>
                        <span className={classes.InvoicingVal} style={{ color: "#64748b", fontSize: "0.95rem" }}>
                          Laskutusdataa ei vielä kytketty
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 4. Palveluntarjoajien ansaintamallit & tilijakauma - Vain Primary/Operator */}
            {!isServiceProvider && (
              <div className={classes.FullWidthCard} style={{ marginTop: "2rem" }}>
                <div className={classes.BreakdownHeaderRow}>
                  <div>
                    <h3>Palveluntarjoajien ansaintamallit ja tilijakauma</h3>
                    <p className={classes.CardSubtitle}>
                      Alustan palveluntarjoajien jakautuminen kuukausilaskutukseen (MONTH) ja mikromaksuihin (MICRO) sekä tunnuksellisten yritysten osuus kannasta.
                    </p>
                  </div>
                  <div className={classes.AdoptionBadge}>
                    <span className={classes.AdoptionBadgeLabel}>Tunnuksellisten osuus:</span>
                    <span className={classes.AdoptionBadgeValue}>{adoptionPct.toFixed(1)}%</span>
                    <span className={classes.AdoptionBadgeCount}>({credentialedTotal} / {allTotal} yritystä)</span>
                  </div>
                </div>

                {/* 4 sarakkeen erittelykortisto */}
                <div className={classes.ProviderBreakdownGrid}>
                  {/* 1. Tunnukselliset – MONTH */}
                  <div className={`${classes.ProviderBreakdownCard} ${classes.CardCredentialed}`}>
                    <div className={classes.BreakdownCardHeader}>
                      <span className={classes.CardScopeBadge}>Tunnukselliset</span>
                      <span className={`${classes.PaymentBadge} ${classes.PaymentBadgeMonth}`}>MONTH</span>
                    </div>
                    <div className={classes.BreakdownCardBody}>
                      <div className={classes.BreakdownValues}>
                        <span className={classes.BreakdownCount}>{credentialedMonth} kpl</span>
                        <span className={classes.BreakdownPercent}>({credentialedMonthPct.toFixed(1)}%)</span>
                      </div>
                      <div className={classes.BreakdownDescription}>
                        Aktiiviset tunnukselliset kk-tilaajat
                      </div>
                    </div>
                  </div>

                  {/* 2. Tunnukselliset – MICRO */}
                  <div className={`${classes.ProviderBreakdownCard} ${classes.CardCredentialed}`}>
                    <div className={classes.BreakdownCardHeader}>
                      <span className={classes.CardScopeBadge}>Tunnukselliset</span>
                      <span className={`${classes.PaymentBadge} ${classes.PaymentBadgeMicro}`}>MICRO</span>
                    </div>
                    <div className={classes.BreakdownCardBody}>
                      <div className={classes.BreakdownValues}>
                        <span className={classes.BreakdownCount}>{credentialedMicro} kpl</span>
                        <span className={classes.BreakdownPercent}>({credentialedMicroPct.toFixed(1)}%)</span>
                      </div>
                      <div className={classes.BreakdownDescription}>
                        Tunnukselliset mikromaksuyritykset
                      </div>
                    </div>
                  </div>

                  {/* 3. Kaikki – MONTH */}
                  <div className={`${classes.ProviderBreakdownCard} ${classes.CardAll}`}>
                    <div className={classes.BreakdownCardHeader}>
                      <span className={`${classes.CardScopeBadge} ${classes.BadgeAll}`}>Kaikki</span>
                      <span className={`${classes.PaymentBadge} ${classes.PaymentBadgeMonth}`}>MONTH</span>
                    </div>
                    <div className={classes.BreakdownCardBody}>
                      <div className={classes.BreakdownValues}>
                        <span className={classes.BreakdownCount}>{allMonth} kpl</span>
                        <span className={classes.BreakdownPercent}>({allMonthPct.toFixed(1)}%)</span>
                      </div>
                      <div className={classes.BreakdownDescription}>
                        Kaikki kannan kk-yritykset (sis. skreipatut)
                      </div>
                    </div>
                  </div>

                  {/* 4. Kaikki – MICRO */}
                  <div className={`${classes.ProviderBreakdownCard} ${classes.CardAll}`}>
                    <div className={classes.BreakdownCardHeader}>
                      <span className={`${classes.CardScopeBadge} ${classes.BadgeAll}`}>Kaikki</span>
                      <span className={`${classes.PaymentBadge} ${classes.PaymentBadgeMicro}`}>MICRO</span>
                    </div>
                    <div className={classes.BreakdownCardBody}>
                      <div className={classes.BreakdownValues}>
                        <span className={classes.BreakdownCount}>{allMicro} kpl</span>
                        <span className={classes.BreakdownPercent}>({allMicroPct.toFixed(1)}%)</span>
                      </div>
                      <div className={classes.BreakdownDescription}>
                        Kaikki kannan mikroyritykset (sis. skreipatut)
                      </div>
                    </div>
                  </div>
                </div>

                <div className={classes.SubSectionHeader}>
                  <h4>Viikoittainen ansaintamallijakauman kehitys</h4>
                </div>
                <div className={classes.SvgWrapper}>
                  <svg 
                    viewBox={`0 0 ${providerChartDimensions.width} ${providerChartDimensions.height}`}
                    className={classes.InteractiveSvg}
                  >
                    <defs>
                      <linearGradient id="subBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0.3" />
                      </linearGradient>
                      <linearGradient id="microBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3" />
                      </linearGradient>
                    </defs>

                    {/* Taustaviivat */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
                      const y = providerChartDimensions.paddingTop + 
                        (providerChartDimensions.height - providerChartDimensions.paddingTop - providerChartDimensions.paddingBottom) * ratio;
                      return (
                        <line 
                          key={index}
                          x1={providerChartDimensions.paddingLeft}
                          y1={y}
                          x2={providerChartDimensions.width - providerChartDimensions.paddingRight}
                          y2={y}
                          stroke="rgba(255,255,255,0.06)"
                          strokeWidth="1"
                        />
                      );
                    })}

                    {/* Stacked Bars */}
                    {comboData.map((d, index) => {
                      const totalWeeks = comboData.length;
                      const chartWidth = providerChartDimensions.width - providerChartDimensions.paddingLeft - providerChartDimensions.paddingRight;
                      const colWidth = chartWidth / totalWeeks;
                      
                      const x = providerChartDimensions.paddingLeft + colWidth * index + (colWidth * 0.2);
                      const barWidth = colWidth * 0.6;
                      
                      const chartHeight = providerChartDimensions.height - providerChartDimensions.paddingTop - providerChartDimensions.paddingBottom;
                      
                      const subHeight = (d.subscriptionProviders / maxProviders) * chartHeight;
                      const microHeight = (d.microProviders / maxProviders) * chartHeight;

                      const subY = providerChartDimensions.height - providerChartDimensions.paddingBottom - subHeight;
                      const microY = subY - microHeight;
                      const totalProviders = d.subscriptionProviders + d.microProviders;

                      return (
                        <g 
                          key={index}
                          onMouseEnter={() => setHoveredProviderIndex(index)}
                          onMouseLeave={() => setHoveredProviderIndex(null)}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Subscription part */}
                          {subHeight > 0 && (
                            <rect 
                              x={x}
                              y={subY}
                              width={barWidth}
                              height={subHeight}
                              fill="url(#subBarGrad)"
                              rx={subHeight > 3 ? 2 : 0}
                              style={{ 
                                fillOpacity: hoveredProviderIndex === index ? 1 : 0.85,
                                transition: "all 0.3s ease"
                              }}
                            />
                          )}
                          {/* Micro part */}
                          {microHeight > 0 && (
                            <rect 
                              x={x}
                              y={microY}
                              width={barWidth}
                              height={microHeight}
                              fill="url(#microBarGrad)"
                              rx={microHeight > 3 ? 2 : 0}
                              style={{ 
                                fillOpacity: hoveredProviderIndex === index ? 1 : 0.85,
                                transition: "all 0.3s ease"
                              }}
                            />
                          )}
                          {/* Kokonaismäärä palkin päällä */}
                          {totalProviders > 0 && (
                            <text
                              x={x + barWidth / 2}
                              y={microY - 6}
                              textAnchor="middle"
                              fill="#475569"
                              fontSize="10"
                              fontWeight="bold"
                            >
                              {totalProviders}
                            </text>
                          )}
                          {/* Osuuksien määrät palkkien sisällä */}
                          {subHeight > 14 && (
                            <text
                              x={x + barWidth / 2}
                              y={subY + subHeight / 2 + 4}
                              textAnchor="middle"
                              fill="#fff"
                              fontSize="9"
                              fontWeight="bold"
                            >
                              {d.subscriptionProviders}
                            </text>
                          )}
                          {microHeight > 14 && (
                            <text
                              x={x + barWidth / 2}
                              y={microY + microHeight / 2 + 4}
                              textAnchor="middle"
                              fill="#fff"
                              fontSize="9"
                              fontWeight="bold"
                            >
                              {d.microProviders}
                            </text>
                          )}
                          {/* Viikon akselinteksti */}
                          {index % (totalWeeks > 15 ? 2 : 1) === 0 && (
                            <text 
                              x={x + barWidth/2}
                              y={providerChartDimensions.height - 15}
                              textAnchor="middle"
                              fill="#888"
                              fontSize="11"
                            >
                              {d.weekLabel}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    <text x={10} y={providerChartDimensions.paddingTop + 10} fill="#888" fontSize="10" fontWeight="bold">Yritykset kpl</text>
                  </svg>
                </div>

                {/* Siisti, dynaaminen Tooltip/Tietolaatikko palveluntarjoajien ansaintamallijakaumalle */}
                <div className={classes.ChartTooltipBox}>
                  {hoveredProviderIndex !== null ? (
                    <div className={classes.TooltipContent}>
                      <span className={classes.TooltipWeek}>
                        {comboData[hoveredProviderIndex].weekLabel} ({comboData[hoveredProviderIndex].startDate})
                      </span>
                      <div className={classes.TooltipRows}>
                        <div>
                          <strong>Yhteensä:</strong>
                          <span style={{ color: "#0f172a", fontWeight: "bold" }}>
                            {comboData[hoveredProviderIndex].subscriptionProviders + comboData[hoveredProviderIndex].microProviders} yritystä
                          </span>
                        </div>
                        <div>
                          <strong>Kk-maksu (MONTH):</strong>
                          <span style={{ color: "#10b981", fontWeight: "bold" }}>
                            {comboData[hoveredProviderIndex].subscriptionProviders} kpl ({((comboData[hoveredProviderIndex].subscriptionProviders / Math.max(comboData[hoveredProviderIndex].subscriptionProviders + comboData[hoveredProviderIndex].microProviders, 1)) * 100).toFixed(0)}%)
                          </span>
                        </div>
                        <div>
                          <strong>Mikromaksu (MICRO):</strong>
                          <span style={{ color: "#3b82f6", fontWeight: "bold" }}>
                            {comboData[hoveredProviderIndex].microProviders} kpl ({((comboData[hoveredProviderIndex].microProviders / Math.max(comboData[hoveredProviderIndex].subscriptionProviders + comboData[hoveredProviderIndex].microProviders, 1)) * 100).toFixed(0)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={classes.TooltipPlaceholder}>
                      Vie hiiri kuvaajan palkkien päälle nähdäksesi viikkokohtaiset määrät ja suhteet.
                    </div>
                  )}
                </div>
                
                {/* Selite legend */}
                <div style={{ display: "flex", gap: "2rem", justifyContent: "center", marginTop: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ display: "inline-block", width: "12px", height: "12px", borderRadius: "2px", background: "#10b981" }} />
                    <span style={{ fontSize: "0.9rem", color: "#555" }}>Kuukausilaskutus (`MONTH`)</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ display: "inline-block", width: "12px", height: "12px", borderRadius: "2px", background: "#3b82f6" }} />
                    <span style={{ fontSize: "0.9rem", color: "#555" }}>Mikromaksu (`MICRO`)</span>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "5rem 0", color: "#777" }}>
            Valitse palvelu ladataksesi raportointi- ja tilastotiedot.
          </div>
        )}
      </div>
    </>
  );
};

export default Reports;
